// ============================================================
// TERROR — V'YEN'S GAUNTLET  (the run engine)
// ------------------------------------------------------------
// H29. The Endless Depths left the Inner Minerium and became
// V'yen's third mode. This file is the part of that move that is
// NEW: the run. Everything the Depths already knew — the seven
// ranks, the five gatekeepers, ELO, the procedural brain, the
// trophy table — stayed exactly where it was in EndlessDepths.js
// and is imported by the screen, not by this file.
//
// caP'n, July 28: "It will work like Mortal Kombat's totem battle
// system, you will do gauntlets at a time. First one enemy, then
// you must fight 3 in a row but your Anima remains at the level it
// was previously... and move up the ranks till we get to our final
// boss."
//
// ── THE ONE MECHANIC ────────────────────────────────────────
// ANIMA CARRIES BETWEEN BOUTS. Nothing else does.
//
// That sentence is the entire mode. A ladder of separate matches
// is just a ladder; a ladder where you walk into bout three on the
// 11 Anima you limped out of bout two with is a SURVIVAL, and the
// decisions in bout one stop being local. Spending eight Anima to
// win comfortably now is a debt you carry to the end of the rung.
//
// Everything else resets, and that was caP'n's explicit call
// (July 29): *"no it does not carry, every Solrend you start over
// brand new except your Anima."* Deck, hand, Khao, field, all of
// it fresh. This is load-bearing rather than generous — the
// Grimoire DEPLETES as you draw, so a deck that carried its
// depletion would put you into bout three of a seven-bout rung
// drawing nothing. The mode would be a coin flip about deck size
// rather than a test of play. The totem is what wears you down;
// your library is not.
//
// ── AND ANIMA RESETS EVERY RUNG ─────────────────────────────
// caP'n: *"within a rung, eventually you'll get some rungs that
// are just insane so yeah every rung it refreshes."*
//
// Which is the difference between a hard mode and an impossible
// one. Carried across the whole ladder, one expensive rung poisons
// every rung above it forever and there is no route back — the run
// is dead and has to keep being played. Per-rung, the pressure is
// real and bounded, and a seven-bout rung is allowed to be brutal
// precisely because it starts you whole.
//
// ── WHY THE TOTEM IS ROLLED UP FRONT ────────────────────────
// The whole stack of opponents is generated when the run STARTS
// and stored with it. Two reasons, one design and one anti-abuse:
// a totem you can see is a totem you can plan against, which is
// the entire reason the format is a totem and not a queue; and a
// stack rolled bout-by-bout could be re-rolled by backing out of
// the screen until a soft opponent appeared.
//
// ── WHY THE RUN LIVES IN FIREBASE ───────────────────────────
// A bout is a full round trip out of this screen — Plaza tears the
// gauntlet down, the duel mounts, and something has to still know
// what rung you were on when you come back. Component state cannot
// survive that. The upside is bigger than the requirement, though:
// because the run is a document rather than a prop, App.js can
// read it directly at match-creation time to seed your carried
// Anima, and again at exit to record the bout. Not one line of
// Plaza.js had to learn what a gauntlet is.
//
// Per IE_FIREBASE_LAW §1 a new profile-level node needs no rules
// deploy — the hard `$extra` lock lives only on collection/$cardId.
// ============================================================

import { ref, get, set, remove, runTransaction } from "firebase/database";
import { db } from "./firebase";
// H30 (multiplayer expansion) — sourcing the AI seat's REAL deck for
// Gauntlet-Duo. Neither of these creates the cycle the note below
// warns about: gameManager.js and starterDecks.js are both leaves
// (gameManager only reaches firebase/replay; starterDecks reaches
// nothing at all), so importing them here is a one-way street.
import { getStarterDeck } from "./starterDecks";
import { drawAndDepleteCards } from "./gameManager";
import { STARTING_ANIMA } from "./rules";

// ── THE LADDER ──────────────────────────────────────────────
// Seven rungs against the seven ranks, five gatekeepers standing
// at the five transitions between them.
//
// Rung 0 is ONE bout and has no boss. It is the taster — the shape
// of the mode taught in the cheapest possible way, because "you
// fight three in a row on one Anima pool" is a sentence nobody
// believes until the second bout opens and their Anima is still
// where they left it.
//
// The last bout of every rung that HAS a boss is that boss. So a
// three-bout rung is two procedural adversaries and then a named
// one, and the gatekeeper is fought on whatever Anima the two in
// front of it left you. That is the fight the mode exists for.
//
// `id` matches the RANKS id in EndlessDepths.js on purpose — the
// screen reads difficulty and colour off the rank rather than this
// table duplicating them, so the two can never drift.
//
// THE FINAL BOSS IS NOT MINTED YET. Rung 6 is `endless: true`,
// which is honest to what Solrend already is ("There is no top.
// There is only deeper") and avoids inventing a Lord to stand at
// the top of the ladder — that is caP'n's call, not this file's.
// When it arrives it is ONE FIELD on the last row.
export const RUNGS = [
  { id: "escling",    bouts: 1, boss: null,          title: "The Taster" },
  { id: "beb",        bouts: 3, boss: "gk_suhnker",  title: "The Outer Gate" },
  { id: "champion",   bouts: 3, boss: "gk_zhefir",   title: "The Watchpost" },
  { id: "lord",       bouts: 5, boss: "gk_tyrent",   title: "The Long Descent" },
  { id: "escendant",  bouts: 5, boss: "gk_vael",     title: "The Rail" },
  { id: "aenarchist", bouts: 7, boss: "gk_ruk",      title: "The Last Gate" },
  { id: "solrend",    bouts: 7, boss: null,          title: "Solrend", endless: true },
];

export const RUNG_BY_ID = Object.fromEntries(RUNGS.map((r, i) => [r.id, { ...r, index: i }]));

// Where every run begins, and where every rung begins. Matches the
// duel's own opening Anima in gameManager.createMatch — if that
// number ever moves, this one follows it, because "you start a
// rung exactly as fresh as you start a normal duel" is the promise.
export const START_ANIMA = STARTING_ANIMA; // R01 — one catalogue, one number

// A win at 1 Anima is still a win, and the next bout opens at 1.
// The ceiling mirrors the duel's own maxAnima.
//
// ZERO IS A SENTINEL, NOT A VALUE, and this is written out because
// it looks like a bug and is not. You cannot WIN a duel on zero
// Anima — zero is ruin. So a zero arriving here only ever means
// App.js could not read the match back and passed its default. That
// case falls open to a full pool: a bout that opens slightly too
// easy is recoverable, a bout that opens already ruined is not.
const clampAnima = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return START_ANIMA;   // see above
  return Math.max(1, Math.min(50, Math.round(n)));
};

// ── WHICH RUNGS ARE OPEN TO YOU ─────────────────────────────
// Derived from the rank your ELO already earned, never stored.
// You may descend to any rung at or below your standing — replaying
// a cleared rung is allowed on purpose. It is the only way to
// practise a totem you just lost, and refusing it would make the
// mode punish experimentation.
export function highestRungFor(rankId) {
  const i = RUNGS.findIndex((r) => r.id === rankId);
  return i < 0 ? 0 : i;
}

// ── BUILDING A RUN ──────────────────────────────────────────
// `makeOpponent(boutIndex)` is injected rather than imported, and
// that is deliberate: it keeps this file free of any dependency on
// EndlessDepths.js, which imports this one. A cycle between an
// engine and its screen is the kind of thing that works in dev and
// then resolves to `undefined` in a production bundle.
export function buildRun({ rungIndex, elo, makeOpponent }) {
  const rung = RUNGS[rungIndex];
  if (!rung) return null;

  const totem = [];
  for (let i = 0; i < rung.bouts; i++) {
    const isBoss = !!rung.boss && i === rung.bouts - 1;
    totem.push(isBoss
      ? { gatekeeperId: rung.boss, boss: true }
      : { opponent: makeOpponent(i), boss: false });
  }

  return {
    rungIndex,
    rungId:    rung.id,
    bout:      0,
    anima:     START_ANIMA,
    totem,
    eloAtStart: Number(elo) || 0,
    startedAt: Date.now(),
  };
}

// ── THE STATE MACHINE ───────────────────────────────────────
// Pure. Takes a run and what happened; returns the next run and a
// word for what it was. Every caller — the screen, App.js's exit
// flow, and anything that comes later — gets the same answer,
// which is the whole reason this is a function and not three
// copies of an if-else in three files.
//
//   "next"         another bout, on the Anima you carried out
//   "rung_cleared" the totem is down
//   "run_over"     you were ruined; the run is gone
export function advanceRun(run, { won, endAnima }) {
  if (!run) return { run: null, outcome: "run_over" };
  if (!won)  return { run: null, outcome: "run_over" };

  const bout = run.bout + 1;
  if (bout >= run.totem.length) return { run: null, outcome: "rung_cleared" };

  return {
    run: { ...run, bout, anima: clampAnima(endAnima) },
    outcome: "next",
  };
}

export const currentBout = (run) => (run && run.totem ? run.totem[run.bout] || null : null);
export const boutsLeft   = (run) => (run && run.totem ? run.totem.length - run.bout : 0);

// ═══════════════════════════════════════════════════════════════
// PERSISTENCE — profiles/{uid}/gauntlet
// ═══════════════════════════════════════════════════════════════
//   run/          the live run, deleted the moment it ends
//   cleared/{id}  append-only stamp per rung ever beaten
//   runs, best    counters, for the screen to have something to say
const runRef  = (uid) => ref(db, `profiles/${uid}/gauntlet/run`);
const nodeRef = (uid) => ref(db, `profiles/${uid}/gauntlet`);

export async function loadRun(uid) {
  if (!uid) return null;
  try {
    const snap = await get(runRef(uid));
    const v = snap.exists() ? snap.val() : null;
    // A run whose totem did not survive the round trip is not a run.
    // Better to drop it than to hand the screen a half object and
    // discover the hole three renders later.
    if (!v || !Array.isArray(v.totem) || !v.totem.length) return null;
    return v;
  } catch (e) {
    console.warn("[gauntlet] loadRun failed:", e);
    return null;
  }
}

export async function saveRun(uid, run) {
  if (!uid || !run) return;
  try { await set(runRef(uid), run); }
  catch (e) { console.warn("[gauntlet] saveRun failed:", e); }
}

export async function clearRun(uid) {
  if (!uid) return;
  try { await remove(runRef(uid)); }
  catch (e) { console.warn("[gauntlet] clearRun failed:", e); }
}

export async function loadGauntletProfile(uid) {
  const empty = { cleared: {}, runs: 0, clears: 0, deepest: -1 };
  if (!uid) return empty;
  try {
    const snap = await get(nodeRef(uid));
    if (!snap.exists()) return empty;
    const v = snap.val() || {};
    return {
      cleared: v.cleared || {},
      runs:    v.runs    || 0,
      clears:  v.clears  || 0,
      deepest: typeof v.deepest === "number" ? v.deepest : -1,
    };
  } catch (e) {
    console.warn("[gauntlet] loadGauntletProfile failed:", e);
    return empty;
  }
}

export async function bumpRunsStarted(uid) {
  if (!uid) return;
  try {
    await runTransaction(ref(db, `profiles/${uid}/gauntlet/runs`), (n) => (n || 0) + 1);
  } catch (e) { console.warn("[gauntlet] bumpRunsStarted failed:", e); }
}

// A rung clear is stamped on an EXACT child path with a transaction
// that aborts if the stamp is already there — the same shape every
// reward write in this codebase uses since the data-loss incident.
// `deepest` is a separate max-write rather than a counter, because
// clearing rung 2 after rung 5 must not walk your standing backwards.
export async function recordRungCleared(uid, rungIndex) {
  if (!uid) return { firstClear: false };
  const rung = RUNGS[rungIndex];
  if (!rung) return { firstClear: false };

  let firstClear = false;
  try {
    const res = await runTransaction(
      ref(db, `profiles/${uid}/gauntlet/cleared/${rung.id}`),
      (cur) => (cur ? undefined : Date.now()),   // undefined = abort, already stamped
    );
    firstClear = !!res.committed;

    await runTransaction(ref(db, `profiles/${uid}/gauntlet/clears`), (n) => (n || 0) + 1);
    await runTransaction(ref(db, `profiles/${uid}/gauntlet/deepest`),
      (cur) => Math.max(typeof cur === "number" ? cur : -1, rungIndex));
  } catch (e) {
    console.warn("[gauntlet] recordRungCleared failed:", e);
  }
  return { firstClear };
}

// ── THE HANDOFF App.js NEEDS ────────────────────────────────
// Called from handleExit with the Anima the duel actually ended
// on. Loads, advances, writes, and hands back the outcome so the
// screen can show the right face when the player lands back on it.
// Kept here rather than in App.js so the rules of a run live in
// exactly one file — App.js should not be a second place where
// somebody can decide what a loss costs.
export async function recordBout(uid, { won, endAnima }) {
  const run = await loadRun(uid);
  if (!run) return { outcome: "no_run", run: null };

  const { run: next, outcome } = advanceRun(run, { won, endAnima });

  if (outcome === "next") {
    await saveRun(uid, next);
    return { outcome, run: next, was: run };
  }

  await clearRun(uid);
  if (outcome === "rung_cleared") {
    const { firstClear } = await recordRungCleared(uid, run.rungIndex);
    await parkOutcome(uid, { outcome, rungId: run.rungId, rungIndex: run.rungIndex,
                             bouts: run.totem.length, firstClear });
    return { outcome, run: null, was: run, firstClear };
  }
  await parkOutcome(uid, { outcome, rungId: run.rungId, rungIndex: run.rungIndex,
                           bouts: run.totem.length, atBout: run.bout + 1 });
  return { outcome, run: null, was: run };
}

// ── THE OUTCOME HAS NOWHERE TO BE RETURNED TO ───────────────
// A run ends while the gauntlet screen is UNMOUNTED — the duel took
// its place on the way in, and Plaza will rebuild the screen from
// scratch on the way out. So "the totem fell" cannot be a return
// value and cannot be component state; by the time anything could
// read it, the thing that would have read it does not exist.
//
// So it is parked on the profile and COLLECTED, once, by whoever
// mounts next. That is a mailbox rather than a state field, and the
// read deletes it — an outcome that survived being seen would show
// the same celebration every time the screen opened.
//
// The happy side effect is that the celebration lands on the totem
// screen instead of as a Plaza toast, which is where it belonged
// anyway: the totem falling should be shown where the totem is.
const outcomeRef = (uid) => ref(db, `profiles/${uid}/gauntlet/lastOutcome`);

async function parkOutcome(uid, payload) {
  if (!uid) return;
  try { await set(outcomeRef(uid), { ...payload, at: Date.now() }); }
  catch (e) { console.warn("[gauntlet] parkOutcome failed:", e); }
}

// Read-and-delete. Named `take` rather than `get` because the
// deletion is the point, not a side effect.
export async function takeLastOutcome(uid) {
  if (!uid) return null;
  try {
    const snap = await get(outcomeRef(uid));
    if (!snap.exists()) return null;
    const v = snap.val();
    await remove(outcomeRef(uid));
    return v && v.outcome ? v : null;
  } catch (e) {
    console.warn("[gauntlet] takeLastOutcome failed:", e);
    return null;
  }
}

// The value App.js seeds onto seat 0 at match creation. Bout zero
// always opens at the standard 30 — reading the stored `anima` for
// it would be harmless today but would silently become a bug the
// day anything writes to a run before its first fight.
export function animaForBout(run) {
  if (!run || run.bout === 0) return START_ANIMA;
  return clampAnima(run.anima);
}

// ═══════════════════════════════════════════════════════════════
// H30 — THE GAUNTLET-DUO AI SEAT'S REAL DECK
// ═══════════════════════════════════════════════════════════════
// V'yen's kiosk (Plaza.js) needs seat 2's actual grimoire before it
// will create a "gauntlet2v2" match — the CURRENT rung's own
// opponent, never a copy of the host's deck. The run document IS the
// rung, so this file is the only honest place that answer can come
// from; nothing here is invented, it is all read off the totem the
// player already rolled.
//
// ── WHY THIS TOUCHES EndlessDepths.js AND HOW ───────────────────
// A procedural bout already carries everything it needs —
// `entry.opponent.starterDeckId` was stamped by generateOpponent()
// when the totem was built. A GATEKEEPER bout does not: the totem
// only stores `gatekeeperId`, and the row that maps a gatekeeper id
// to a name and a starterDeckId is the GATEKEEPERS table, which lives
// in EndlessDepths.js, not here.
//
// A static import of that table would be the exact cycle the note at
// the top of this file already warns about (EndlessDepths.js imports
// THIS file at the top level) — "a cycle between an engine and its
// screen is the kind of thing that works in dev and then resolves to
// undefined in a production bundle." So the lookup below is a
// DYNAMIC import instead, reached only when a boss bout is actually
// current, and only after this function has already been called —
// which is well after both modules have finished their own top-level
// evaluation. That is precisely the case a dynamic import is safe for
// and a static one is not.
//
// ── SYNC OR ASYNC ────────────────────────────────────────────────
// Async, and it has to be: `loadRun` is a Firebase read, and a
// gatekeeper bout needs the dynamic import above on top of that.
// Plaza.js's stub already does `await getGauntletOpponentDeck(profile)`,
// so the two sides agree without either having to change.
//
// ── WHAT "CANNOT BE DETERMINED" MEANS HERE ──────────────────────
// null, never a throw, whenever there's nothing honest to hand back:
// no uid on the profile, no run in flight (the player has not started
// a Gauntlet run), a bout that resolves to neither a gatekeeper nor a
// procedural opponent, or a `starterDeckId` that doesn't match any
// STARTER_DECKS row. Plaza.js's stub already treats null as "refuse
// to seat this AI and say so" — an opponent playing an empty deck is
// a worse failure than not seating one at all.
export async function getGauntletOpponentDeck(profile) {
  const uid = profile?.uid;
  if (!uid) return null;

  const run = await loadRun(uid);
  if (!run) return null;

  const entry = currentBout(run);
  if (!entry) return null;

  // ── WHO'S ACROSS THE TABLE ─────────────────────────────────────
  // Mirrors resolveBout() in EndlessDepths.js exactly (gatekeeperId
  // first, opponent object otherwise) rather than re-deriving that
  // branch — it is the one place "what is the current bout's foe"
  // is already decided, and a second copy of that if/else is a second
  // place it can drift.
  let name, starterDeckId, difficulty;
  if (entry.gatekeeperId) {
    const { GATEKEEPERS } = await import("./EndlessDepths");   // dynamic — see note above
    const gk = GATEKEEPERS.find((g) => g.id === entry.gatekeeperId);
    if (!gk) return null;
    name = gk.name;
    starterDeckId = gk.starterDeckId;
    difficulty = gk.difficulty;
  } else if (entry.opponent) {
    name = entry.opponent.name;
    starterDeckId = entry.opponent.starterDeckId;
    difficulty = entry.opponent.difficulty;
  } else {
    return null;
  }

  const starterDeck = starterDeckId ? getStarterDeck(starterDeckId) : null;
  if (!starterDeck) return null;

  // Same deal-parity rule buildNPCPlayerData (NPC.js) uses for every
  // other NPC seat in this game: the hand comes OUT of the grimoire,
  // never dealt on top of a full, undepleted copy of it — "hand +
  // grimoire = full deck" (H15). Getting this wrong is exactly the
  // bug H15 fixed for the single-player NPC seat; a second AI seat is
  // not an excuse to reintroduce it.
  const { hand, grimoire } = drawAndDepleteCards(starterDeck.grimoire || {}, [], 5);

  return {
    name,
    // THE BRAIN'S BRIEF (2026-09-06) — the Duo boss plays at its own
    // authored skill, not the engine's default. NPC.js reads
    // "Beb" | "Champion" | "Lord"; the ladder stores the same words.
    difficulty: difficulty || "Champion",
    grimoire,
    luxmoire: starterDeck.luxmoire || {},
    prophecy: starterDeck.prophecy || null,
    hand,
    curios:   starterDeck.curios   || [null, null],
    esclings: starterDeck.esclings || null,
  };
}
